// src/utils/simpleCache.js
// Same public API as before: getCached, clearCache, clearAllCache, getCacheStats.
// Improvements: concurrent calls for the same key share ONE request, empty/failed
// results are never cached, and old entries are swept from localStorage.
const CACHE_PREFIX = 'app_cache_v2_';
const cache = new Map();    // key -> { value, timestamp }
const inflight = new Map(); // key -> Promise

const SWEEP_MAX_AGE = 6 * 60 * 60 * 1000; // entries older than 6h are always expired
const PURGE_FLAG = 'app_cache_purged_user_docs_v1';

// null / undefined / "" usually mean "failed" or "not found" - never keep those
const isCacheable = (v) => v !== null && v !== undefined && v !== '';

// One-time cleanup on startup
(function sweepStorage() {
    try {
        const now = Date.now();
        const purgeOldUserDocs = !localStorage.getItem(PURGE_FLAG);

        Object.keys(localStorage).forEach((k) => {
            if (!k.startsWith(CACHE_PREFIX)) return;
            try {
                // Older versions cached whole user documents (may include emails)
                if (purgeOldUserDocs && k.startsWith(CACHE_PREFIX + 'user-')) {
                    localStorage.removeItem(k);
                    return;
                }
                const parsed = JSON.parse(localStorage.getItem(k));
                if (!parsed || !isCacheable(parsed.value) || now - parsed.timestamp > SWEEP_MAX_AGE) {
                    localStorage.removeItem(k);
                }
            } catch (e) {
                try { localStorage.removeItem(k); } catch (e2) { }
            }
        });

        if (purgeOldUserDocs) localStorage.setItem(PURGE_FLAG, '1');
    } catch (e) {
        // localStorage unavailable (private mode etc.) - cache just stays in memory
    }
})();

/**
 * Get cached value or fetch a new one
 * @param {string} key - Cache key
 * @param {Function} fetchFn - Function to fetch data if not cached
 * @param {number} ttl - Time to live in milliseconds (default: 50 minutes)
 */
export const getCached = async (key, fetchFn, ttl = 50 * 60 * 1000) => {
    const fullKey = CACHE_PREFIX + key;
    const now = Date.now();

    // 1. Memory cache (fastest)
    const memCached = cache.get(fullKey);
    if (memCached && now - memCached.timestamp < ttl) {
        return memCached.value;
    }

    // 2. LocalStorage (survives reloads)
    try {
        const stored = localStorage.getItem(fullKey);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (isCacheable(parsed.value) && now - parsed.timestamp < ttl) {
                cache.set(fullKey, parsed);
                return parsed.value;
            }
            try { localStorage.removeItem(fullKey); } catch (e) { }
        }
    } catch (e) {
        console.warn('Cache read error:', e);
    }

    // 3. Already being fetched right now? Share that request.
    if (inflight.has(fullKey)) {
        return inflight.get(fullKey);
    }

    // 4. Fetch from the network and store
    const request = (async () => {
        const value = await fetchFn();

        if (isCacheable(value)) {
            const entry = { value, timestamp: Date.now() };
            cache.set(fullKey, entry);
            try {
                localStorage.setItem(fullKey, JSON.stringify(entry));
            } catch (e) {
                console.warn('Cache write error (Storage full?):', e);
            }
        }
        return value;
    })();

    inflight.set(fullKey, request);
    try {
        return await request;
    } finally {
        inflight.delete(fullKey);
    }
};

/** Clear a specific cache entry */
export const clearCache = (key) => {
    const fullKey = CACHE_PREFIX + key;
    cache.delete(fullKey);
    inflight.delete(fullKey);
    try {
        localStorage.removeItem(fullKey);
    } catch (e) { }
};

/** Clear all cache */
export const clearAllCache = () => {
    cache.clear();
    inflight.clear();
    try {
        Object.keys(localStorage).forEach((k) => {
            if (k.startsWith(CACHE_PREFIX)) {
                try { localStorage.removeItem(k); } catch (e) { }
            }
        });
    } catch (e) { }
};

/** Cache statistics (for debugging) */
export const getCacheStats = () => ({
    size: cache.size,
    keys: Array.from(cache.keys()),
});

export default {
    getCached,
    clearCache,
    clearAllCache,
    getCacheStats,
};